// Custom.
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-3xl border border-transparent text-base font-medium whitespace-nowrap transition-[color,background-color,border-color,transform,translate,scale,rotate] duration-200 ease-in-out select-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden active:enabled:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        transparent: 'hover:enabled:bg-transparent data-[state=open]:bg-transparent',
        default:
          'bg-primary text-primary-foreground shadow-xs hover:enabled:bg-primary/80 data-[state=open]:bg-primary/50',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:enabled:bg-destructive/80 data-[state=open]:bg-destructive/50',
        outline:
          'border-input bg-transparent shadow-xs hover:enabled:bg-accent/80 hover:enabled:text-accent-foreground data-[state=open]:bg-accent/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:enabled:bg-secondary/70 data-[state=open]:bg-secondary/50',
        accent:
          'bg-accent text-accent-foreground shadow-xs hover:enabled:bg-accent/80 data-[state=open]:bg-accent/50',
        ghost:
          'bg-transparent hover:enabled:bg-accent/80 hover:enabled:text-accent-foreground data-[state=open]:bg-accent/50',
        link: 'text-accent decoration-accent/80 underline-offset-4 hover:enabled:text-accent/80 hover:enabled:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-8 px-3 text-xs',
        sm: 'h-8 px-3 text-sm',
        lg: 'h-10 px-4',
        icon: 'size-9 rounded-full',
        'icon-sm': 'size-8 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        type="button"
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
